// Pure builders: (codes, files, params) -> { type, data, options } for react-chartjs-2's <Chart>.
// Every number here must match ProjectChartPlanService::describeFigure on the backend.

export const fallbackColors = [
    "#aeea84", "#fcd39e", "#a8a1e9", "#ff6384", "#36a2eb", "#ffce56", "#b8e994",
    "#f6e58d", "#686de0", "#303952", "#f5cd79", "#574b90", "#f3a683", "#574b90"
];

const colorOf = (code, idx) => code.color || fallbackColors[idx % fallbackColors.length];
const fileName = (file) => file.name || `File ${file.id}`;

/** code × file excerpt counts, via excerpt.analysis_parent_id === file.id */
export function countsMatrix(codes = [], files = []) {
    return codes.map(code => files.map(file =>
        (code.excerpts || []).filter(e => e.analysis_parent_id === file.id).length
    ));
}

const totals = (codes) => codes.map(code => (code.excerpts || []).length);

const baseOptions = (title) => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
        title: { display: false, text: title },
        legend: { display: false },
        datalabels: { display: false }
    }
});

export function buildBar(codes, files, params = {}) {
    let rows = codes.map((code, idx) => ({ label: code.name, value: totals([code])[0], color: colorOf(code, idx) }));
    rows.sort((a, b) => b.value - a.value);
    if (params.top_n) rows = rows.slice(0, Number(params.top_n));
    const options = baseOptions();
    options.indexAxis = "y";
    options.layout = { padding: { right: 32 } }; // room for the end-anchored value label of the longest bar
    options.scales = { x: { beginAtZero: true, ticks: { precision: 0 } } };
    options.plugins.datalabels = { display: true, anchor: "end", align: "end", color: "#111", formatter: v => v };
    return {
        type: "bar",
        data: { labels: rows.map(r => r.label), datasets: [{ data: rows.map(r => r.value), backgroundColor: rows.map(r => r.color) }] },
        options
    };
}

export function buildPolar(codes) {
    return {
        type: "polarArea",
        data: {
            labels: codes.map(c => c.name),
            datasets: [{ data: totals(codes), backgroundColor: codes.map(colorOf) }]
        },
        options: {
            ...baseOptions(),
            scales: { r: { ticks: { display: false }, beginAtZero: true } },
            plugins: { legend: { display: true, position: "right" }, datalabels: { display: true, color: "#111", formatter: v => (v > 0 ? v : "") } }
        }
    };
}

/** rows × cols heatmap. values[i][j] is the cell for rowLabels[i], colLabels[j]. */
export function buildMatrix(rowLabels, colLabels, values, { suffix = "", rgb = "37, 99, 235" } = {}) {
    const cells = [];
    values.forEach((row, i) => row.forEach((v, j) => cells.push({ x: colLabels[j], y: rowLabels[i], v })));
    const max = Math.max(1, ...cells.map(c => c.v));
    const options = baseOptions();
    options.scales = {
        x: { type: "category", labels: colLabels, offset: true, grid: { display: false }, ticks: { autoSkip: false } },
        y: { type: "category", labels: rowLabels, offset: true, grid: { display: false }, reverse: true }
    };
    options.plugins.tooltip = { callbacks: { title: () => "", label: ctx => `${ctx.raw.y} — ${ctx.raw.x}: ${ctx.raw.v}${suffix}` } };
    options.plugins.datalabels = {
        display: true,
        color: ctx => (ctx.dataset.data[ctx.dataIndex].v / max > 0.55 ? "#fff" : "#111"),
        formatter: (_, ctx) => { const v = ctx.dataset.data[ctx.dataIndex].v; return v ? `${v}${suffix}` : ""; }
    };
    return {
        type: "matrix",
        data: {
            datasets: [{
                data: cells,
                backgroundColor: ctx => `rgba(${rgb}, ${0.08 + 0.85 * (ctx.dataset.data[ctx.dataIndex].v / max)})`,
                borderColor: "#fff",
                borderWidth: 1,
                width: ({ chart }) => (chart.chartArea || {}).width / Math.max(1, colLabels.length) - 2,
                height: ({ chart }) => (chart.chartArea || {}).height / Math.max(1, rowLabels.length) - 2
            }]
        },
        options
    };
}

const normalizeColumns = (matrix) => {
    const totals = matrix[0].map((_, j) => matrix.reduce((s, row) => s + row[j], 0));
    return matrix.map(row => row.map((n, j) => (totals[j] ? Math.round(n * 100 / totals[j]) : 0)));
};

export function buildHeatmap(codes, files, params = {}) {
    const perFile = params.normalize === "per_file";
    const matrix = countsMatrix(codes, files);
    const values = perFile && matrix.length ? normalizeColumns(matrix) : matrix;
    return buildMatrix(codes.map(c => c.name), files.map(fileName), values, { suffix: perFile ? "%" : "" });
}

/** Mirrors ProjectChartPlanService::groupLabels on the backend: keep the two in sync. */
export function groupLabels(value) {
    if (Array.isArray(value)) return value.map(v => String(v).trim()).filter(Boolean);
    if (typeof value === "boolean") return [value ? "Yes" : "No"];
    if (value === null || value === undefined) return [];
    const label = String(value).trim();
    return label ? [label] : [];
}

/** attribute name → sorted [{label, fileIds}] using analysis.speaker_subjects → subject → property value. */
export function attributeGroups(files, subjects, attributeName) {
    const byUuid = Object.fromEntries((subjects || []).map(s => [s.uuid, s]));
    const groups = {};
    files.forEach(file => {
        let map = file.speaker_subjects;
        if (typeof map === "string") { try { map = JSON.parse(map); } catch { map = null; } }
        Object.values(map || {}).forEach(uuid => {
            const pv = byUuid[uuid]?.property_values?.find(v => v.property_definition?.name === attributeName);
            groupLabels(pv?.value).forEach(label => { (groups[label] ||= new Set()).add(file.id); });
        });
    });
    return Object.keys(groups).sort().map(label => ({ label, fileIds: groups[label] }));
}

export function buildCodeByAttribute(codes, files, subjects, params = {}) {
    const groups = attributeGroups(files, subjects, params.attribute);
    const matrix = codes.map(code => groups.map(g =>
        (code.excerpts || []).filter(e => g.fileIds.has(e.analysis_parent_id)).length
    ));
    const perGroup = params.normalize === "per_group";
    const values = perGroup && matrix.length && groups.length ? normalizeColumns(matrix) : matrix;
    return buildMatrix(codes.map(c => c.name), groups.map(g => g.label), values, { suffix: perGroup ? "%" : "", rgb: "5, 150, 105" });
}

/** codes × codes: number of files in which both codes appear (diagonal = files with the code). */
export function buildCooccurrence(codes, files) {
    const present = countsMatrix(codes, files).map(row => row.map(n => n > 0));
    const values = present.map(a => present.map(b => a.filter((x, j) => x && b[j]).length));
    const labels = codes.map(c => c.name);
    return buildMatrix(labels, labels, values, { rgb: "124, 58, 237" });
}

export function buildStackedBar(codes, files, params = {}) {
    const matrix = countsMatrix(codes, files);
    const byCode = params.orientation === "by_code";
    const normalize = !!params.normalize;
    const labels = byCode ? codes.map(c => c.name) : files.map(fileName);
    const groupTotals = byCode
        ? matrix.map(row => row.reduce((s, n) => s + n, 0))
        : files.map((_, j) => matrix.reduce((s, row) => s + row[j], 0));
    const pct = (n, g) => (normalize ? (groupTotals[g] ? Math.round(n * 1000 / groupTotals[g]) / 10 : 0) : n);
    const datasets = byCode
        ? files.map((file, j) => ({ label: fileName(file), data: codes.map((_, i) => pct(matrix[i][j], i)), backgroundColor: fallbackColors[j % fallbackColors.length] }))
        : codes.map((code, i) => ({ label: code.name, data: files.map((_, j) => pct(matrix[i][j], j)), backgroundColor: colorOf(code, i) }));
    const options = baseOptions();
    options.scales = {
        x: { stacked: true },
        y: { stacked: true, beginAtZero: true, max: normalize ? 100 : undefined, ticks: { precision: 0, callback: v => (normalize ? `${v}%` : v) } }
    };
    options.plugins.legend = { display: true, position: "bottom" };
    return { type: "bar", data: { labels, datasets }, options };
}

export function buildTreemap(codes) {
    const byUuid = Object.fromEntries(codes.map(c => [c.uuid, c]));
    const tree = codes.map((code, idx) => {
        const parent = code.parent ? byUuid[code.parent] : null;
        return {
            parent: parent ? parent.name : code.name,
            code: parent ? code.name : (codes.some(c => c.parent === code.uuid) ? `${code.name} (own)` : code.name),
            value: totals([code])[0],
            color: colorOf(parent || code, idx)
        };
    }).filter(n => n.value > 0);
    const options = baseOptions();
    options.plugins.tooltip = { callbacks: { title: () => "", label: ctx => `${ctx.raw.g || ctx.raw._data.code}: ${ctx.raw.v}` } };
    return {
        type: "treemap",
        data: {
            datasets: [{
                tree,
                key: "value",
                groups: ["parent", "code"],
                spacing: 1,
                borderWidth: 1,
                borderColor: "#fff",
                backgroundColor: ctx => (ctx.type !== "data" ? "transparent" : (ctx.raw.l === 0 ? "rgba(0,0,0,0.06)" : ctx.raw._data.children?.[0]?.color || fallbackColors[ctx.dataIndex % fallbackColors.length])),
                captions: { display: true, color: "#111", font: { weight: "bold" } },
                labels: { display: true, color: "#111", formatter: ctx => (ctx.type === "data" ? [ctx.raw.g, String(ctx.raw.v)] : "") }
            }]
        },
        options
    };
}

/** words: [{text, weight}] most frequent first; font size scaled 14..64px */
export function buildWordCloud(words = []) {
    const max = Math.max(1, ...words.map(w => w.weight));
    const min = Math.min(max, ...words.map(w => w.weight));
    const size = (w) => (max === min ? 40 : Math.round(14 + 50 * (w.weight - min) / (max - min)));
    return {
        type: "wordCloud",
        data: {
            labels: words.map(w => w.text),
            datasets: [{ data: words.map(size), color: words.map((_, i) => fallbackColors[i % fallbackColors.length]), family: "sans-serif" }]
        },
        options: {
            ...baseOptions(),
            plugins: { legend: { display: false }, datalabels: { display: false }, tooltip: { callbacks: { label: ctx => `${ctx.label}: ${words[ctx.dataIndex]?.weight}` } } }
        }
    };
}

export function buildChart(figure, codes = [], files = [], subjects = []) {
    const params = figure.params || {};
    switch (figure.type) {
        case "wordcloud": return buildWordCloud(figure.words || []);
        case "code_by_attribute": return buildCodeByAttribute(codes, files, subjects, params);
        case "cooccurrence": return buildCooccurrence(codes, files);
        case "bar": return buildBar(codes, files, params);
        case "heatmap": return buildHeatmap(codes, files, params);
        case "stacked_bar": return buildStackedBar(codes, files, params);
        case "treemap": return buildTreemap(codes);
        case "polar": return buildPolar(codes);
        default: return null;
    }
}
